import Config from '../_config/config';
import myAxios from './axiositeachWrapper';
import type {
  EnquiryImportCatalog,
  EnquiryImportConfig,
} from '../pages/enquiry-ingestion/utils/enquiryColumns';

const API_BASE_URL = Config.nodeSiteUrl;

const getAuthHeaders = (): HeadersInit => {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const currentUser = localStorage.getItem('currentUser');
  if (currentUser && currentUser !== 'undefined') {
    try {
      const user = JSON.parse(currentUser);
      if (user.token) {
        headers['Authorization'] = `Bearer ${user.token}`;
      }
    } catch (error) {
      console.error('Failed to parse currentUser from localStorage:', error);
    }
  }
  return headers;
};

export interface StudentIngestionRow {
  EnquiryId: string;
  StudentCode: string;
  BatchCode: string;
  UserName: string;
  Password: string;
}

export interface ServerValidationError {
  row: number;
  field: string;
  message: string;
}

export interface ValidateStudentDataResponse {
  success: boolean;
  data?: {
    errors: ServerValidationError[];
    totalRows: number;
    invalidBatchCodes: string[];
    invalidEnquiryIds: number[];
    alreadyEnrolledCodes: string[];
    alreadyEnrolledEnquiries: number[];
  };
  message?: string;
}

export const validateStudentData = async (
  rows: StudentIngestionRow[]
): Promise<ValidateStudentDataResponse> => {
  const headers = getAuthHeaders();
  console.log('[ingestion.service] Sending validation request with auth:', headers['Authorization'] ? 'Bearer token present' : 'NO AUTH TOKEN');

  const response = await fetch(`${API_BASE_URL}v1/ingestion/validate-student-data`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ rows }),
  });

  if (!response.ok) {
    let errorDetail = '';
    try {
      const errorBody = await response.json();
      errorDetail = errorBody?.error?.message || errorBody?.message || JSON.stringify(errorBody);
    } catch {
      errorDetail = response.statusText;
    }
    throw new Error(`Server validation failed (${response.status}): ${errorDetail}`);
  }

  return response.json();
};

export interface EnrollmentRowResult {
  row: number;
  success: boolean;
  enrollmentId?: number;
  studentCode?: string;
  userName?: string;
  password?: string;
  parentUserName?: string;
  error?: string;
}

export interface EnrollStudentsResponse {
  success: boolean;
  summary?: { total: number; enrolled: number; failed: number };
  results?: EnrollmentRowResult[];
  message?: string;
}

export const enrollStudents = async (
  rows: StudentIngestionRow[]
): Promise<EnrollStudentsResponse> => {
  const headers = getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}v1/ingestion/enroll-students`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ rows }),
  });

  if (!response.ok) {
    let errorDetail = '';
    try {
      const errorBody = await response.json();
      errorDetail = errorBody?.error?.message || errorBody?.message || JSON.stringify(errorBody);
    } catch {
      errorDetail = response.statusText;
    }
    throw new Error(`Enrollment failed (${response.status}): ${errorDetail}`);
  }

  return response.json();
};

// --- Enquiry Ingestion: per-tenant column configuration ---

const describeAxiosError = (err: any, action: string): Error => {
  const status = err?.response?.status;
  const detail =
    err?.response?.data?.error?.message ||
    err?.response?.data?.message ||
    err?.message ||
    'Unknown error';
  return new Error(`${action} failed (${status ?? 'network'}): ${detail}`);
};

/** Full column catalogue + this tenant's saved selection. */
export const getEnquiryImportConfig = async (): Promise<EnquiryImportCatalog> => {
  try {
    const response = await myAxios.get<any>(`${API_BASE_URL}v1/ingestion/enquiry-import-config`);
    return response.data.data as EnquiryImportCatalog;
  } catch (err: any) {
    throw describeAxiosError(err, 'Loading enquiry import configuration');
  }
};

export const saveEnquiryImportConfig = async (
  enabledColumns: string[],
  mandatoryColumns: string[]
): Promise<EnquiryImportConfig> => {
  try {
    const response = await myAxios.put<any>(
      `${API_BASE_URL}v1/ingestion/enquiry-import-config`,
      { enabledColumns, mandatoryColumns }
    );
    return response.data.data.config as EnquiryImportConfig;
  } catch (err: any) {
    throw describeAxiosError(err, 'Saving enquiry import configuration');
  }
};

// --- Enquiry Ingestion ---

export interface ValidateEnquiryDataResponse {
  success: boolean;
  data?: {
    errors: ServerValidationError[];
    totalRows: number;
    mandatoryColumns: string[];
    /** Column key -> values that matched no master row (soft ones stored as NULL). */
    unresolvedValues: Record<string, string[]>;
  };
  message?: string;
}

export const validateEnquiryData = async (
  rows: Record<string, string>[],
  isProgramBased = true
): Promise<ValidateEnquiryDataResponse> => {
  try {
    const response = await myAxios.post<any>(
      `${API_BASE_URL}v1/ingestion/validate-enquiry-data`,
      { rows, isProgramBased }
    );
    return response.data as ValidateEnquiryDataResponse;
  } catch (err: any) {
    const status = err?.response?.status;
    const errorDetail =
      err?.response?.data?.error?.message ||
      err?.response?.data?.message ||
      err?.message ||
      'Unknown error';
    throw new Error(`Server validation failed (${status ?? 'network'}): ${errorDetail}`);
  }
};

export interface EnquiryIngestRowResult {
  row: number;
  success: boolean;
  enquiryId?: number;
  enrolled?: boolean;
  enrollmentId?: number;
  studentCode?: string;
  userName?: string;
  password?: string;
  parentUserName?: string;
  error?: string;
}

export interface IngestEnquiriesResponse {
  success: boolean;
  summary?: { total: number; created: number; failed: number };
  results?: EnquiryIngestRowResult[];
  message?: string;
}

export const ingestEnquiries = async (
  rows: Record<string, string>[],
  fileBase64: string,
  fileName: string,
  isProgramBased = true
): Promise<IngestEnquiriesResponse> => {
  try {
    const response = await myAxios.post<any>(
      `${API_BASE_URL}v1/ingestion/ingest-enquiries`,
      { rows, fileBase64, fileName, isProgramBased }
    );
    return response.data as IngestEnquiriesResponse;
  } catch (err: any) {
    const status = err?.response?.status;
    const errorDetail =
      err?.response?.data?.error?.message ||
      err?.response?.data?.message ||
      err?.message ||
      'Unknown error';
    throw new Error(`Enquiry ingestion failed (${status ?? 'network'}): ${errorDetail}`);
  }
};
